import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';
const PAGE_SIZE = 20;

const STATUS_MAP = {
  completed: '完成', failed: '失败', pending: '等待', processing: '处理中'
};

function AdminTasks() {
  const { session } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);
      const res = await fetch(`${API_BASE_URL}/admin/tasks?${params}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const result = await res.json();
      if (result.success) {
        setTasks(result.data.tasks);
        setTotal(result.data.total);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [session, page, statusFilter, typeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/tasks/stats`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const result = await res.json();
      if (result.success) setStats(result.data);
    } catch (e) { console.error(e); }
  }, [session]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const fmtMoney = (n) => `¥${(parseFloat(n) || 0).toFixed(2)}`;
  const fmtTime = (t) => new Date(t).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      {/* 统计摘要 */}
      {stats && (
        <div className="ad-stats-grid" style={{ marginBottom: 20 }}>
          {Object.entries(stats.byStatus).map(([k, v]) => (
            <div className="ad-stat-card" key={k}>
              <div className="ad-stat-label">{STATUS_MAP[k] || k}</div>
              <div className="ad-stat-value">{v}</div>
            </div>
          ))}
        </div>
      )}

      {stats && (
        <div className="ad-term" style={{ marginBottom: 20 }}>
          <div className="ad-term-bar">
            <div className="ad-term-dots">
              <span className="ad-term-dot ad-term-dot--red" />
              <span className="ad-term-dot ad-term-dot--yellow" />
              <span className="ad-term-dot ad-term-dot--green" />
            </div>
            <span className="ad-term-title">task_stats_by_type</span>
          </div>
          <div className="ad-term-body">
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {Object.entries(stats.byType).map(([k, v]) => (
                <div key={k} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-dim)' }}>{k}: </span>
                  <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            {Object.keys(stats.byLanguage).length > 0 && (
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
                {Object.entries(stats.byLanguage).map(([k, v]) => (
                  <div key={k} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    <span style={{ color: 'var(--text-dim)' }}>{k}: </span>
                    <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 任务列表 */}
      <div className="ad-term">
        <div className="ad-term-bar">
          <div className="ad-term-dots">
            <span className="ad-term-dot ad-term-dot--red" />
            <span className="ad-term-dot ad-term-dot--yellow" />
            <span className="ad-term-dot ad-term-dot--green" />
          </div>
          <span className="ad-term-title">tasks — {total} 条记录</span>
        </div>
        <div className="ad-term-body">
          <div className="ad-toolbar">
            <select className="ad-filter-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">全部状态</option>
              <option value="pending">等待</option>
              <option value="processing">处理中</option>
              <option value="completed">完成</option>
              <option value="failed">失败</option>
            </select>
            <select className="ad-filter-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">全部类型</option>
              <option value="audio">音频</option>
              <option value="srt">SRT</option>
            </select>
          </div>

          {loading ? (
            <div className="ad-loading">加载中...</div>
          ) : tasks.length === 0 ? (
            <div className="ad-empty">暂无任务</div>
          ) : (
            <>
              <div className="ad-table-wrap">
                <table className="ad-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>用户</th>
                      <th>类型</th>
                      <th>状态</th>
                      <th>费用</th>
                      <th>语言</th>
                      <th>时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontSize: 10 }}>{t.id.substring(0, 8)}</td>
                        <td>{t.user_email || t.user_id?.substring(0, 8)}</td>
                        <td>{t.character_count ? 'SRT' : '音频'}</td>
                        <td><span className={`ad-badge ad-badge--${t.status}`}>{STATUS_MAP[t.status] || t.status}</span></td>
                        <td>{t.cost != null ? fmtMoney(parseFloat(t.cost)) : '-'}</td>
                        <td>{t.target_language || '-'}</td>
                        <td>{fmtTime(t.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="ad-pagination">
                  <button className="ad-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>上一页</button>
                  <span className="ad-page-info">{page} / {totalPages}</span>
                  <button className="ad-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>下一页</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminTasks;
